import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  ShieldAlert,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ImageOff,
  Link2,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { useAnalysisDetails, useApproveAnalysis, useRejectAnalysis } from '../hooks/useAnalysis'
import { artworkApi } from '../api/artworkApi'
import type { RiskLevel } from '../api/analysisApi'

const RISK_BADGE: Record<string, string> = {
  critical: 'bg-red-600 text-white border-transparent',
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-green-100 text-green-800 border-green-200',
  unknown: 'bg-muted text-muted-foreground',
}

const SOURCE_BADGE: Record<string, string> = {
  exact_match: 'bg-red-100 text-red-800 border-red-200',
  partial_match: 'bg-amber-100 text-amber-800 border-amber-200',
  similar: 'bg-muted text-muted-foreground',
  visually_similar: 'bg-muted text-muted-foreground',
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url.slice(0, 40)
  }
}

/** Every outbound link and image here points at a third-party site we do not
 *  control. noreferrer on the links and no-referrer on the images keep the
 *  admin's URL — which contains ids — out of those sites' logs. */
function EvidenceLink({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-start gap-2 text-sm hover:underline"
    >
      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
      <span className="min-w-0">{children}</span>
    </a>
  )
}

/** Remote thumbnails fail often — hotlink protection, dead CDNs, expired
 *  signed URLs. A broken image icon that still links out is more honest than
 *  a blank square, because the URL is the evidence even when the picture is
 *  gone. */
function MatchThumb({ url, label }: { url: string; label: string }) {
  const [broken, setBroken] = useState(false)

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      className="group block overflow-hidden rounded-md border bg-muted transition-colors hover:border-foreground/30"
    >
      <div className="flex aspect-square items-center justify-center">
        {broken ? (
          <div className="flex flex-col items-center gap-1 p-2 text-center">
            <ImageOff className="h-5 w-5 text-muted-foreground" />
            <span className="text-[10px] leading-tight text-muted-foreground">{hostOf(url)}</span>
          </div>
        ) : (
          <img
            src={url}
            alt={label}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setBroken(true)}
          />
        )}
      </div>
      <p className="truncate border-t px-2 py-1 text-[10px] text-muted-foreground">{hostOf(url)}</p>
    </a>
  )
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count?: number
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-baseline gap-2 font-serif text-lg font-semibold">
        {title}
        {count !== undefined && (
          <span className="text-sm font-sans font-normal text-muted-foreground">{count}</span>
        )}
      </h3>
      {children}
    </section>
  )
}

export function AnalysisEvidenceDialog({
  analysisId,
  onOpenChange,
}: {
  analysisId: number | null
  onOpenChange: (open: boolean) => void
}) {
  const { data, isLoading, error } = useAnalysisDetails(analysisId)
  const { mutate: approve, isPending: approving } = useApproveAnalysis()
  const { mutate: reject, isPending: rejecting } = useRejectAnalysis()
  const [notes, setNotes] = useState('')

  const close = () => {
    setNotes('')
    onOpenChange(false)
  }

  const vision = data?.vision_raw_response
  const assessment = data?.copyright_assessment
  const risk = (assessment?.riskLevel || 'unknown') as RiskLevel
  const pages = vision?.pagesWithMatchingImages || []
  const exact = vision?.fullMatchingImages || []
  const partial = vision?.partialMatchingImages || []
  const similar = vision?.visuallySimilarImages || []
  const sources = data?.sources || []

  return (
    <Dialog open={analysisId !== null} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        {isLoading && <LoadingSpinner />}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>Could not load the analysis.</AlertDescription>
          </Alert>
        )}

        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-3 pr-6 font-serif text-2xl">
                {data.title || data.file_name || `Artwork ${data.artwork_id}`}
                <Badge variant="outline" className={RISK_BADGE[risk] || RISK_BADGE.unknown}>
                  <ShieldAlert className="mr-1 h-3 w-3" />
                  {risk} risk
                </Badge>
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {data.artistic_name || data.artist_name} · artwork #{data.artwork_id} · analysis v
                {data.version}
                {assessment?.confidence != null && (
                  <> · {Math.round(assessment.confidence * 100)}% confidence</>
                )}
              </p>
            </DialogHeader>

            <div className="grid gap-6 md:grid-cols-[220px_1fr]">
              {/* The uploaded piece, so the matches can be compared against it
                  without leaving the dialog. */}
              <div className="space-y-2">
                <div className="overflow-hidden rounded-md border bg-muted">
                  <img
                    src={artworkApi.getArtworkImageUrl(data.artwork_id)}
                    alt={data.title || 'Artwork'}
                    className="aspect-square w-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src.includes('placeholder-image')) return
                      e.currentTarget.src = '/placeholder-image.svg'
                    }}
                  />
                </div>
                <p className="text-center text-xs text-muted-foreground">Uploaded artwork</p>

                {data.canonical_metadata && (
                  <div className="space-y-1 pt-2 text-xs">
                    {data.canonical_metadata.style && (
                      <p>
                        <span className="text-muted-foreground">Style: </span>
                        {data.canonical_metadata.style}
                      </p>
                    )}
                    {data.canonical_metadata.medium && (
                      <p>
                        <span className="text-muted-foreground">Medium: </span>
                        {data.canonical_metadata.medium}
                      </p>
                    )}
                    {!!data.canonical_metadata.tags?.length && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {data.canonical_metadata.tags.slice(0, 8).map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="min-w-0 space-y-6">
                <Section title="Assessment">
                  <p className="text-sm leading-relaxed">
                    {assessment?.reasoning || 'No reasoning recorded.'}
                  </p>
                  {data.llm_diagnostics?.model && (
                    <p className="text-xs text-muted-foreground">
                      {data.llm_diagnostics.model}
                      {data.llm_diagnostics.confidence != null && (
                        <>
                          {' '}
                          · self-reported confidence{' '}
                          {Math.round(data.llm_diagnostics.confidence * 100)}%
                        </>
                      )}
                    </p>
                  )}
                </Section>

                {!!exact.length && (
                  <Section title="Exact matches" count={exact.length}>
                    <p className="text-xs text-muted-foreground">
                      Vision found the same image at these URLs.
                    </p>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {exact.slice(0, 15).map((u) => (
                        <MatchThumb key={u} url={u} label="Exact match" />
                      ))}
                    </div>
                  </Section>
                )}

                {!!partial.length && (
                  <Section title="Partial matches" count={partial.length}>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {partial.slice(0, 10).map((u) => (
                        <MatchThumb key={u} url={u} label="Partial match" />
                      ))}
                    </div>
                  </Section>
                )}

                {!!pages.length && (
                  <Section title="Pages carrying this image" count={pages.length}>
                    <div className="space-y-2">
                      {pages.slice(0, 20).map((p) => (
                        <div key={p.url} className="border-b pb-2 last:border-0">
                          <EvidenceLink url={p.url}>
                            <span className="block truncate font-medium">
                              {p.pageTitle || p.url}
                            </span>
                            <span className="text-xs text-muted-foreground">{hostOf(p.url)}</span>
                          </EvidenceLink>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {!!sources.length && (
                  <Section title="Cited by the model" count={sources.length}>
                    <p className="text-xs text-muted-foreground">
                      The specific URLs the assessment above was based on.
                    </p>
                    <div className="space-y-2">
                      {sources.map((s, i) => (
                        <div key={`${s.url}-${i}`} className="flex items-start gap-2">
                          {s.type && (
                            <Badge
                              variant="outline"
                              className={`shrink-0 text-[10px] ${SOURCE_BADGE[s.type] || ''}`}
                            >
                              {s.type.replace('_', ' ')}
                            </Badge>
                          )}
                          <EvidenceLink url={s.url}>
                            <span className="break-all text-xs">{s.url}</span>
                          </EvidenceLink>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {!!similar.length && (
                  <Section title="Visually similar" count={similar.length}>
                    <p className="text-xs text-muted-foreground">
                      Saturation signal, not evidence of copying.
                    </p>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {similar.slice(0, 10).map((u) => (
                        <MatchThumb key={u} url={u} label="Visually similar" />
                      ))}
                    </div>
                  </Section>
                )}

                {data.competitive_presence && (
                  <Section title="Marketplace presence">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Saturation: </span>
                      {data.competitive_presence.saturationLevel || 'unknown'}
                    </p>
                    {data.competitive_presence.insights && (
                      <p className="text-sm leading-relaxed">
                        {data.competitive_presence.insights}
                      </p>
                    )}
                    {!!data.competitive_presence.marketplaceFindings?.length && (
                      <div className="space-y-1">
                        {data.competitive_presence.marketplaceFindings.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{f.platform || 'Unknown platform'}</span>
                            {f.similarCount != null && (
                              <span className="text-muted-foreground">· {f.similarCount}</span>
                            )}
                            {f.url && (
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-auto text-xs hover:underline"
                              >
                                open
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>
                )}

                {!!vision?.webEntities?.length && (
                  <Section title="What Vision recognised">
                    <div className="flex flex-wrap gap-1">
                      {vision.webEntities.slice(0, 12).map((e, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">
                          {e.description}
                          {e.score != null && (
                            <span className="ml-1 text-muted-foreground">{e.score.toFixed(2)}</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              {data.admin_status !== 'pending_review' && (
                <Alert>
                  <AlertDescription className="text-sm">
                    Already {data.admin_status === 'approved' ? 'cleared' : 'held'}
                    {data.admin_notes ? ` — ${data.admin_notes}` : ''}
                  </AlertDescription>
                </Alert>
              )}
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Note (required to hold). If you hold, the artist sees this."
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Holding shows the artist &ldquo;Needs review&rdquo; with this note, so write it to
                them: what the concern is and what would clear it.
              </p>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={approving || rejecting}
                  onClick={() =>
                    approve(
                      { analysisId: data.id, notes: notes.trim() || undefined },
                      { onSuccess: close }
                    )
                  }
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Clear
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={!notes.trim() || approving || rejecting}
                  onClick={() =>
                    reject({ analysisId: data.id, notes: notes.trim() }, { onSuccess: close })
                  }
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Hold
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
